import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { COLORS, FONT_FAMILY } from '../../../app/common/constants/styles';
import USStates from '../../../i18n/us-states/us_states.json';
import store from '../../../utils/store';
import { TextHelpers } from '../../../utils/textHelpers';
import { IStateMap } from '../../../app/common/fields/state/state';
import { IAddress } from './address.interface';
import { PostalCodeValidation } from '../../../app/common/fields/postalcode/PostalCodeValidation';
import PostalAddress from '../../../declarations/js-modules/i18n-postal-address.d';
import { SupportedLanguage } from '../../../config/constants/supportedLanguage.enum';

export class AddressFormatter {
    static FormattedAddressWrapper = styled.div`
        background: ${COLORS.grey2};
        border-radius: 3px;
        font-size: 0.875rem;
        margin: 10px 0;
        padding: 15px 20px;
    `;

    static EmptyWrapper = styled.div``;

    static Bold = styled.span`
        ${FONT_FAMILY.multipleBoldCss};
        font-weight: 700;
    `;

    static FormattedAddressLine = styled.div`
        margin-top: 5px;
        word-break: break-word;
    `;

    public static getCountryName(countryCode: string): string {
        const state = store.getState();
        const countries =
            state.countries.countriesMap && state.countries.countriesMap[state.app.locale];
        const country = countries && countries[countryCode];
        return country ? country.name : '';
    }

    public static getStateKey(stateCode: string): string {
        const stateCodeSplit = stateCode.toUpperCase().split('-');
        if (stateCodeSplit.length > 1 && stateCodeSplit[0] === 'US') {
            return stateCodeSplit[1];
        }
        return stateCodeSplit[0];
    }

    public static getStateName(stateCode: string): string {
        const state = (USStates as IStateMap)[AddressFormatter.getStateKey(stateCode)];
        return state ? state.name : '';
    }

    public static format(
        address: IAddress,
        useShort = false,
        addressTemplate?: string,
    ): string[][] {
        const postalAddress = new PostalAddress();

        postalAddress.addFormat({
            country: 'TW',
            format: [
                ['lastName', 'firstName'],
                ['country'],
                ['postalCode', 'city', 'state'],
                ['address1'],
                ['address2'],
            ],
        });

        postalAddress.setAddress1(address.addressLine1);

        if (!useShort) {
            postalAddress.setAddress2(address.addressLine2);
        }

        postalAddress.setCity(address.city);
        postalAddress.setPostalCode(address.postalCode);
        postalAddress.setCountry(AddressFormatter.getCountryName(address.countryCode));

        if (address.state) {
            postalAddress.setState(address.state);
        } else if (address.stateCode) {
            postalAddress.setState(AddressFormatter.getStateName(address.stateCode));
        }

        postalAddress.setOutputFormat('array');
        postalAddress.setFormat({
            country: addressTemplate ?? address.countryCode,
            type: 'personal',
            useTransforms: false,
        });

        return postalAddress.output();
    }

    public static formatToLine(address: IAddress, addressTemplate?: string): string {
        return AddressFormatter.format(address, false, addressTemplate).reduce<string>(
            (prev: string, line: string[], index: number) =>
                `${prev}${
                    index === 0 || index === line.length - 1 ? '' : ','
                } ${AddressFormatter.formatAddressLine(line)}`,
            '',
        );
    }

    public static formatToReactNode(
        address: IAddress,
        includeName = true,
        isStyled = false,
        useShort = false,
        locale = SupportedLanguage.EN,
        addressTemplate?: string,
    ): ReactNode {
        const FormattedAddressWrapper = isStyled
            ? AddressFormatter.FormattedAddressWrapper
            : AddressFormatter.EmptyWrapper;

        const formattedAddress: ReactNode = AddressFormatter.format(
            address,
            useShort,
            addressTemplate,
        ).map((line, index) => (
            <AddressFormatter.FormattedAddressLine
                data-qa-id={`address-summary-line-${index}`}
                key={`address-line-${index}`}
            >
                {AddressFormatter.formatAddressLine(line)}
            </AddressFormatter.FormattedAddressLine>
        ));
        const companyName = address.companyName ? (
            <AddressFormatter.FormattedAddressLine data-qa-id="address-summary-company-name">
                {AddressFormatter.trimText(address.companyName)}
            </AddressFormatter.FormattedAddressLine>
        ) : (
            ''
        );

        const reverseNameArr = [
            SupportedLanguage.JA,
            SupportedLanguage.ZH_TW,
            SupportedLanguage.ZH_CN,
        ];
        const personName = (
            <AddressFormatter.FormattedAddressLine data-qa-id="address-summary-person-name">
                {reverseNameArr.includes(locale)
                    ? AddressFormatter.trimText(`${address.lastName} ${address.firstName}`)
                    : AddressFormatter.trimText(`${address.firstName} ${address.lastName}`)}
            </AddressFormatter.FormattedAddressLine>
        );

        return (
            <FormattedAddressWrapper data-qa-id="address-summary">
                <AddressFormatter.Bold>{includeName ? personName : ''}</AddressFormatter.Bold>
                {useShort || addressTemplate === 'TW' ? '' : companyName}
                {formattedAddress}
            </FormattedAddressWrapper>
        );
    }

    public static JPformatToReactNode(
        address: IAddress,
        includeName = true,
        isStyled = false,
        useShort = false,
    ): ReactNode {
        const FormattedAddressWrapper = isStyled
            ? AddressFormatter.FormattedAddressWrapper
            : AddressFormatter.EmptyWrapper;

        let displayPostalCode = address.postalCode;
        if (PostalCodeValidation.validateJPPostalCode(address.postalCode)) {
            displayPostalCode = '〒' + address.postalCode;
        }

        const displayPhoneNumber = address.phoneNumber;

        return (
            <FormattedAddressWrapper data-qa-id="address-summary">
                <AddressFormatter.FormattedAddressLine>
                    {displayPostalCode}
                </AddressFormatter.FormattedAddressLine>
                <AddressFormatter.FormattedAddressLine data-qa-id="address-summary-state-city">
                    {address.state} {address.city}
                </AddressFormatter.FormattedAddressLine>
                <AddressFormatter.FormattedAddressLine data-qa-id="address-summary-addressLine1">
                    {address.addressLine1}
                </AddressFormatter.FormattedAddressLine>
                <AddressFormatter.FormattedAddressLine data-qa-id="address-summary-company-name">
                    {address.companyName}
                </AddressFormatter.FormattedAddressLine>
                <AddressFormatter.FormattedAddressLine data-qa-id="address-summary-jobTitle">
                    {address.positionName}
                </AddressFormatter.FormattedAddressLine>
                <AddressFormatter.FormattedAddressLine data-qa-id="address-summary-phoneNumber">
                    {displayPhoneNumber}
                </AddressFormatter.FormattedAddressLine>
            </FormattedAddressWrapper>
        );
    }

    public static formatAddressLine(line: string[]): string {
        const addressLine = line.join(', ');
        return AddressFormatter.trimText(addressLine);
    }

    public static trimText(text: string): string {
        return TextHelpers.trimText(text, 50);
    }
}
